import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, of } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Metafield, Product, Variant } from '../../../shop/models';

import * as fromShopify from '../../../shop';
import { RouterStateUrl, SetAlternateUrls, SetCanonicalUrl, SetTags, SetTitle, getRouterState } from '../../../store';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { LoadProductsByQuery } from '../../../shop';

import { StringUtility } from '@teamfoster/sdk/text-utility';
import { RouterReducerState } from '@ngrx/router-store';
import { Analyzify } from 'src/app/utility/analyzify.utility';
import { CookiePreferences, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { MetaPixel } from 'src/app/utility/meta-pixel.utility';
import { FormBuilder } from '@angular/forms';
import { REQUEST } from '../../../../express.tokens';
import { Request } from 'express';

import { NotifyMeDialogComponent } from '../../components/notify-me-dialog/notify-me-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { isPlatformBrowser } from '@angular/common';
import { getActiveStorefront } from 'src/app/storefronts';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  private dict = new FromDictionaryPipe(this.lang);

  cookiePrefs$!: Observable<CookiePreferences | null>;
  product$!: Observable<Product | undefined>;
  loading$ = new BehaviorSubject<boolean>(true);
  recommendations$?: Observable<Product[] | undefined>;
  productDetailsLoaded$?: Observable<string[]>;
  productColors$?: Observable<Product[] | undefined>;
  recommendationsLoading$?: Observable<boolean>;
  cartLoading$!: Observable<boolean>;
  patternBtnActive = false;
  detailLoaded = false;
  routerState$!: Observable<RouterReducerState<RouterStateUrl>>;

  activeStorefront$ = this.store.select(getActiveStorefront);

  klaviyoIsSubScribedBackInStock$!: Observable<boolean>;
  klaviyoEmail$!: Observable<string>;

  // TODO klaviyo remove
  fb = inject(FormBuilder);

  private dialog = inject(MatDialog);

  sliderConfig = {
    gap: 5,
    showArrows: true,
    showBullets: true,
    perView: 1,
    startAt: 0,
    listenToEvents: true,
    type: 'carousel',
  };

  // Structured data
  schema: any;
  breadcrumbScheme: any;

  breadcrumbs: Breadcrumb[] = [];

  amount = 1;

  // variants
  variantOptionsLeft$: Observable<Variant[]> = of([]);
  selectedVariant$: Observable<Variant | undefined> = of(undefined);

  //sliderMedia$: Observable<ShopifyMedia[] | undefined> = of(undefined);

  private _unsubscribe = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private lang: LanguageService,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>,
    @Optional() @Inject(REQUEST) private request: Request
  ) {}

  ngOnInit(): void {
    this.cookiePrefs$ = this.store.select(getCookiePreferences);

    this.product$ = this.store.select(fromShopify.getSelectedproduct);
    this.productDetailsLoaded$ = this.store.select(fromShopify.getProductsDetailEntities);
    this.cartLoading$ = this.store.select(fromShopify.getCartLoading);
    this.recommendations$ = this.store.select(fromShopify.getRecommendations);
    this.recommendationsLoading$ = this.store.select(fromShopify.getRecommendationsLoading);
    this.productColors$ = this.store.select(fromShopify.getSelectedProductColors);

    this.routerState$ = this.store.select(getRouterState);

    // get SelectedVariant by queryParams
    this.variantOptionsLeft$ = this.routerState$.pipe(
      withLatestFrom(this.product$),
      map(([router, product]) => {
        if (Object.keys(router.state.queryParams).length) {
          const params = router.state.queryParams;

          const filteredObject = product?.variants?.filter(item => {
            return Object.keys(params)
              .filter(name => product?.options?.filter(a => a.name === name).length)
              .every(queryKey => {
                const queryValue = params[queryKey];
                return item.selectedOptions.some(option => option.name === queryKey && option.value === queryValue);
              });
          });

          return filteredObject || [];
        }

        return product?.variants || [];
      })
    );

    // if only one variant is left, after selecting options, set this as the selected variant
    this.selectedVariant$ = this.variantOptionsLeft$.pipe(map(a => (a.length === 1 ? a[0] : undefined)));

    this.product$
      .pipe(takeUntil(this._unsubscribe), withLatestFrom(this.productDetailsLoaded$, this.routerState$))
      .subscribe(([product, loaded, router]) => {
        if (!product || !loaded.some(a => a === product.handle)) {
          return;
        }

        // Set Scheme for structured data
        this.setProductScheme(product);
        this.setBreadcrumbScheme(product, router);
        this.setBreadcrumbs(product);

        // Get related content
        this.store.dispatch(fromShopify.LoadProductRecommendations({ productId: product.id }));

        // Load related (color) products
        if (product.metafields?.find(a => a?.key === 'kleurvarianten')) {
          const variants: string[] = JSON.parse(product.metafields?.find(a => a?.key === 'kleurvarianten')?.value || '[]');
          const searchQuery = variants
            ?.map((a: string) => {
              const id = a.split('/').slice(-1)[0];
              return `(id:${id})`;
            })
            .join(' OR ');
          if (searchQuery) {
            this.store.dispatch(LoadProductsByQuery({ searchQuery, identifier: `color-variants_${product.id}`, cardType: 'kleurmeta' }));
          }
        }

        if (product && Object.keys(product).length) {
          const hostname = isPlatformBrowser(this.platformId) ? document.location.hostname : this.request?.hostname;

          this.store.dispatch(SetTitle({ title: `${product.seo.title || product.title}` }));
          this.store.dispatch(SetCanonicalUrl({ url: `https://${hostname}/products/${product.handle}` }));
          this.store.dispatch(SetAlternateUrls({ path: `/products/${product.handle}` }));
          this.store.dispatch(
            SetTags({
              properties: {
                'description':
                  StringUtility.Truncate(StringUtility.StripHTML(product?.seo?.description), 157, '...') ||
                  StringUtility.Truncate(StringUtility.StripHTML(product?.descriptionHtml), 157, '...'),
                'og:image': product?.featuredImage?.url,
                'og:title': `
              ${StringUtility.Truncate(StringUtility.StripHTML(product?.seo?.title), 157, '...') || product.title}
            `,
                'og:description':
                  StringUtility.Truncate(StringUtility.StripHTML(product?.seo?.description), 157, '...') ||
                  StringUtility.Truncate(StringUtility.StripHTML(product?.descriptionHtml), 157, '...'),
              },
            })
          );
        }
      });

    this.productDetailsLoaded$.pipe(takeUntil(this._unsubscribe), withLatestFrom(this.product$)).subscribe(([detailsLoaded, product]) => {
      this.detailLoaded = product ? detailsLoaded.includes(product.handle) : false;
      this.cd.detectChanges();
    });

    combineLatest([this.product$, this.selectedVariant$, this.cookiePrefs$, this.productDetailsLoaded$])
      .pipe(
        filter(
          ([product, variant, cookies, detailLoaded]) => !!product && (cookies?.marketing ?? false) && detailLoaded.includes(product.handle)
        ),
        takeUntil(this._unsubscribe)
      )
      .subscribe(([product, selectedVariant]) => {
        this.trackProductViewed(product!, selectedVariant);
        Analyzify.ProductPageView(product!, selectedVariant);
        MetaPixel.ViewContent(product!, selectedVariant);
      });

    this.checkIfSubscribedBackInStock();

    this.klaviyoEmail$ = this.store.select(state => fromShopify.getKlaviyoState(state).email || '');

    //this.sliderMedia$ = this.product$.pipe(
    //  map(product => {
    //    // Add video's
    //    const videoTypes = ['VIDEO']; //'EXTERNALVIDEO'
    //    const videos = product?.media?.filter(a => !videoTypes.includes(a.mediaContentType.toUpperCase())) || [];
    //    const mappedProductImages: ShopifyMedia[] =
    //      product?.images?.map(a => ({
    //        id: a.id,
    //        mediaContentType: 'MEDIAIMAGE',
    //        previewImage: { ...a },
    //      })) || [];

    //    console.log('items?', mappedProductImages);
    //    return [...mappedProductImages, ...videos];
    //  })
    //);
  }

  orderVariant() {
    this.selectedVariant$.pipe(withLatestFrom(this.product$, this.cookiePrefs$), take(1)).subscribe(([variant, product, cookies]) => {
      if (variant) {
        this.store.dispatch(fromShopify.AddToCart({ lines: [{ merchandiseId: variant.id, quantity: this.amount }] }));
        if (cookies?.marketing) {
          Analyzify.AddToCart(product!, variant, this.amount);
          MetaPixel.AddToCart(product!, variant);
        }
      }
    });
  }

  listSpecifications(product: Product) {
    const specs = this.filterEmptySpecs(product);
    const excludedMetaFields = ['kleur_filter', 'kleur_label', 'kleurcode', 'custom_label'];
    const preferredOrder = ['sku', 'ean', 'material', 'papierkwaliteit', 'format', 'product_unit'];

    // Add SEO title to specs
    //const seoTitleAsMetafield: Metafield = {
    //  id: '',
    //  key: 'seo-titel',
    //  value: product.seo.title,
    //  type: 'string',
    //};

    //specs.push(seoTitleAsMetafield);
    // Smart specs from product info
    const collectionsAsSpec = {
      id: '',
      key: 'collecties',
      type: 'links',
      value: '',
      links: product.collections?.map((a: any) => ({ handle: a.handle, title: a.title })),
    };
    const designer = { id: '', key: 'ontwerper', type: 'string', value: product?.vendor };

    const addedSmartSpecs = [...specs, designer, collectionsAsSpec];

    const filterExcludedSpecs = addedSmartSpecs.filter(a => !excludedMetaFields.includes(a.key));
    const filterEmptySpecs = filterExcludedSpecs.filter(a => !excludedMetaFields.includes(a.value));
    const sortedSpecs = filterEmptySpecs.sort((a, b) => {
      const indexA = preferredOrder.indexOf(a.key);
      const indexB = preferredOrder.indexOf(b.key);
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    return sortedSpecs;
  }

  setBreadcrumbs(product: Product) {
    this.breadcrumbs = [];
    const collectionHandle = this.route.snapshot.params['collectionHandle'];

    if (collectionHandle) {
      // this.breadcrumbs.push({
      //   label: 'Collecties',
      //   routerLink: ['/collections'],
      // });
      this.breadcrumbs.push({
        label: product?.collections?.filter((collection: any) => collection.handle === collectionHandle)?.[0]?.title,
        routerLink: ['/', 'collections', collectionHandle],
      });
    }

    // TODO WIP (product type voor dingen als plastic verzendzakken stap?)
    // if (product.productType) {
    //   this.breadcrumbs.push({
    //     label: product.productType,
    //     routerLink: ['/', 'collections', collectionHandle],
    //     params: { type: product.productType },
    //   });
    // }

    this.breadcrumbs.push({
      label: product.seo.title || product.title,
      routerLink: [],
    });
  }

  checkIfSubscribedBackInStock() {
    this.selectedVariant$.subscribe(variant => {
      if (variant) {
        this.klaviyoIsSubScribedBackInStock$ = this.store.select(state =>
          fromShopify.isSubscribed(state, variant.id.split('/').reverse()[0] || '')
        );
      } else {
        this.product$.subscribe(product => {
          this.klaviyoIsSubScribedBackInStock$ = this.store.select(state =>
            fromShopify.isSubscribed(state, product?.variants[0].id.split('/').reverse()[0] || '')
          );
        });
      }
    });
  }

  handleSubscribeBackInStock(email?: string | null) {
    if (!email) {
      return;
    }

    this.selectedVariant$
      .subscribe(variant => {
        if (variant && variant.id.split('/').reverse()[0] != '') {
          this.store.dispatch(
            fromShopify.SubscribeBackInStock({
              email,
              productId: variant.id.split('/').reverse()[0],
            })
          );
        } else {
          this.product$
            .subscribe(product => {
              if (product?.variants[0].id.split('/').reverse()[0] != '') {
                this.store.dispatch(
                  fromShopify.SubscribeBackInStock({
                    email,
                    productId: product?.variants[0].id.split('/').reverse()[0]!,
                  })
                );
              }
            })
            .unsubscribe();
        }
        this.checkIfSubscribedBackInStock();
      })
      .unsubscribe();
  }

  setProductScheme(product: Product) {
    this.schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      'name': product?.title,
      'image': product?.featuredImage?.url,
      'description': product?.seo?.description || product?.description,
      //'sku': this.selectedVariant?.sku,
      'offers': {
        '@type': 'Offer',
        'priceCurrency': product?.priceRange?.maxVariantPrice?.currencyCode || 'EUR',
        'price': product?.priceRange?.minVariantPrice?.amount,
        'availability': product?.availableForSale ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        'seller': {
          '@type': 'Organization',
          'name': product?.vendor,
        },
      },
    };
  }

  setBreadcrumbScheme(product: Product, router: RouterReducerState<RouterStateUrl>) {
    // TODO als we breadcrumbs hebben die daar uithalen
    const urlParts = router.state.url.split('/').filter(a => a);

    this.breadcrumbScheme = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'item': {
            '@type': 'Website',
            '@id': environment.storeDomain,
            'name': environment.storeName,
          },
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'item': {
            '@type': 'CollectionPage',
            '@id': environment.storeDomain + '/' + [...urlParts].slice(0, -2).join('/'),
            'name': urlParts[0],
          },
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'item': {
            '@type': 'CollectionPage',
            '@id': environment.storeDomain + '/' + [...urlParts].slice(0, -1).join('/'),
            'name': urlParts[1],
          },
        },
        {
          '@type': 'ListItem',
          'position': 4,
          'item': {
            '@type': 'WebPage',
            '@id': environment.storeDomain + router.state.url,
            'name': product.title,
          },
        },
      ],
    };
  }

  scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  getUnit(product: Product, selectedVariant?: Variant | null) {
    if (selectedVariant) {
      const variantUnit = selectedVariant.metafields?.find(m => m?.key === 'product_unit_variant')?.value;
      if (variantUnit) {
        return variantUnit;
      }
    }
    return product.metafields?.find(m => m?.key === 'product_unit')?.value;
  }

  get isAvailable$(): Observable<{ code: string; message: string }> {
    return combineLatest(this.variantOptionsLeft$, this.product$).pipe(
      map(([selectedVariants, product]) => {
        if (!product || !product.variants) {
          return { code: 'UNKNOWN', message: 'Not available' };
        }

        if (product.availableForSale === false) {
          return { code: 'PRODUCT_SOLD_OUT', message: 'Product sold out' };
        }

        if (selectedVariants?.length > 1) {
          return { code: 'UNKNOWN_OPTIONS', message: 'Select options first' };
        }

        if (selectedVariants.length === 1) {
          if (!selectedVariants[0].availableForSale) {
            return { code: 'SOLD_OUT', message: 'Sold out' };
          }

          if (selectedVariants[0].availableForSale) {
            return { code: 'AVAILABLE', message: 'Add to cart' };
          }
        }

        return { code: 'UNKNOWN', message: 'Not available' };
      })
    );
  }

  get soldOut$() {
    return combineLatest([this.isAvailable$, this.selectedVariant$, this.product$]).pipe(
      map(([isAvailable, selectedVariant, product]) => {
        return (
          (isAvailable.code !== 'AVAILABLE' && selectedVariant && !selectedVariant.availableForSale && product?.availableForSale) ||
          !product?.availableForSale
        );
      })
    );
  }

  get maxAmount$(): Observable<number> {
    return this.selectedVariant$.pipe(
      map(variant => {
        if (!variant || !variant.availableForSale) {
          return -1;
        }
        // if tracking inventory is disbaled in shopify
        if (variant.availableForSale && variant.quantityAvailable === 0) {
          return 0;
        }
        return variant.quantityAvailable;
      })
    );
  }

  getLabel(prefix: string, label: string, fallback?: string) {
    const dictPlaceholder = prefix + label;
    if (this.dict.transform(dictPlaceholder, false)) {
      return this.dict.transform(dictPlaceholder, false);
    }
    console.warn('CMS dictionary: Label not filled for placeholder:', dictPlaceholder);
    return fallback || label;
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  filterEmptySpecs(product: Product) {
    let { metafields } = product;

    // Get fields outside meta for specs
    this.selectedVariant$.pipe(take(1)).subscribe(selectedVariant => {
      if (!selectedVariant) {
        return;
      }

      // if override some fields from product to variant if they exist
      const variantMetaFields = [
        'papierkwaliteit',
        'finishing',
        'fsc',
        'gelijk_of_mix',
        'handgrepen',
        'material',
        'ontwerp',
        'papierkwaliteit',
        'theme',
        'retourstrip',
        'stijl',
        'vorm',
        'ean',
        'inkt',
        'inside_print',
      ];
      const overrideSpecsfromVariant: { key: string; variantKey?: string }[] = [
        ...variantMetaFields.map((a: string) => ({ key: a })),
        {
          key: 'product_unit',
          variantKey: 'product_unit_variant',
        },
        {
          key: 'format',
          variantKey: 'formaat',
        },
      ];
      const variantFilteredMetaFields: Metafield[] =
        selectedVariant.metafields?.filter(
          (a: Metafield | undefined) => a && overrideSpecsfromVariant.map(b => b['variantKey'] || b.key).includes(a.key) && a.value
        ) || [];
      const renamedVariantMetaFields: Metafield[] = variantFilteredMetaFields.map(f => {
        const productKey = overrideSpecsfromVariant.find(a => (a.variantKey || a.key) === f.key)?.key;
        return { ...f, key: productKey || '' };
      }); // renmae key to product specs key

      //replace metafields with renamedVariantMetaFields if the key is the same
      const filteredMetafields = (metafields?.filter(a => !!a) || []).filter(
        a => !renamedVariantMetaFields.map(b => b.key).includes(a!.key)
      );

      // Add specs from variant which are not metafields
      const variantFields = ['sku', 'barcode'];
      const variantSpecifications: Metafield[] = [];
      variantFields.forEach(key => {
        const value = (<any>selectedVariant)[key];

        if (value) {
          variantSpecifications.push({
            id: key,
            key: key,
            value: value,
            type: 'string',
          });
        }
      });

      metafields = [...filteredMetafields, ...renamedVariantMetaFields, ...variantSpecifications];
    });

    return (metafields?.filter(a => !!a && a.key !== 'kleurvarianten') as Metafield[]) || [];
  }

  isColorVariant(variantOptionName: string) {
    const colorVariantNames = ['kleur', 'color', 'colour'];
    return variantOptionName && colorVariantNames.includes(variantOptionName.toLowerCase());
  }

  trackProductViewed(product: Product, selectedVariant?: Variant) {
    if (!(window as any)['klaviyo']) {
      return;
    }
    const item: any = {
      ProductName: product.title,
      ProductID: product.id,
      SKU: selectedVariant?.sku,
      Categories: product.collections?.map((a: any) => a.title),
      ImageURL: selectedVariant?.image?.url || (product.images && product.images[0].url),
      URL: this.router.url,
      Brand: product.vendor,
      Price: selectedVariant?.price?.amount,
      CompareAtPrice: selectedVariant?.compareAtPrice?.amount,
    };
    (window as any)['klaviyo'].push(['track', 'Viewed Product', item]);

    (window as any)['klaviyo'].push([
      'trackViewedItem',
      {
        Title: product.title,
        ItemId: product.id,
        Categories: product.collections?.map((a: any) => a.title),
        ImageUrl: selectedVariant?.image?.url || (product.images && product.images[0].url),
        Url: this.router.url,
        Metadata: {
          Brand: product.vendor,
          Price: selectedVariant?.price?.amount,
          CompareAtPrice: selectedVariant?.compareAtPrice?.amount,
        },
      },
    ]);
  }

  async notifyMe() {
    const klaviyoEmail = await firstValueFrom(this.klaviyoEmail$);
    const dlgPrefix = 'melding-houdt-me-op-de-hoogte';
    const dlg = this.dialog.open(NotifyMeDialogComponent, {
      data: {
        prefix: dlgPrefix,
        email: klaviyoEmail,
      },
      maxWidth: '600px',
      width: '100%',
    });

    const result = await firstValueFrom(dlg.afterClosed());

    if (typeof result === 'string') {
      this.handleSubscribeBackInStock(result);
    }
  }
}
